import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class RedFlag extends Base implements EntityType {
    redFlagId: uuid;
    redFlagTypeId: uuid;
    checked: boolean;
    boost: boolean;
    notes?: string | null;
    constructor(doc: typeof RedFlag | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            redFlagId: genUUID(),
            redFlagTypeId: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "redFlag";
        // @ts-ignore
        this.redFlagId = doc._redFlagId || doc.redFlagId || doc.redflagid || genUUID();
        // @ts-ignore
        this.redFlagTypeId = doc._redFlagTypeId || doc.redFlagTypeId || doc.redflagtypeid || null;
        // @ts-ignore
        this.checked = util.defaultBoolean([doc.checked, doc._checked, doc.checked, false]);
        // @ts-ignore
        this.boost = util.defaultBoolean([doc.boost, doc._boost, doc.boost, false]);
        // @ts-ignore
        this.notes = doc._notes || doc.notes || null;
    }
    get id(): uuid { return this.redFlagId; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this redFlag"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}]; }
    toLowerCase() { return this.redFlagId; }
    static override getNonRefArrayFields(): string[] { return []; }
    static override getDefaultEntries(): RedFlag[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'redFlag',
    table: 'RED_FLAG',
    comment: `A red flag is a per-entity instance of a client-risk warning, attached to a lead or an active matter. Users check off which warnings apply (e.g. 'unrealistic expectations', 'financial risk'), optionally boost the most severe ones, and add notes. Checked flags feed a weighted risk score that signals how problematic a lead or case is.`,
    fields: {
        redFlagId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this redFlag'
        },
        redFlagTypeId: {
            type: 'uuid',
            entityType: 'redFlagType',
            required: true,
            // OWA-473: deleting a red-flag TYPE removes the concept firm-wide, so
            // its flags are cascade-deleted (status 'D') rather than orphaned by
            // blanking redFlagTypeId. A case-type CHANGE does not delete flags.
            deleteStrategy: 'delete',
            // OWA-415: a lead/case carries at most one flag per type. `unique` enforces
            // a partial unique index on RED_FLAG_CURRENT (firmId, entityType, entityId,
            // redFlagTypeId) so a hydration-race re-seed cannot mint a duplicate flag.
            unique: true,
            comment: 'The base red flag (has the text)'
        },
        checked: {
            type: 'boolean',
            required: false,
            comment: 'Has the user flagged this as meeting their own threshold for concern?'
        },
        boost: {
            type: 'boolean',
            comment: 'Do you want to boost this flag, because it is really bad?'
        },
        notes: {
            type: 'string',
            comment: 'Any clarifying notes on this red flag'
        }
    },
    indexes: [
        {name: 'leads', fn: (m) => m.entityType === 'lead' && m.status !== 'D'},
        {name: 'matters', fn: m => m.entityType === 'matter' && m.status !== 'D'},
    ],
    maps: [
        {
            name: 'entityId',
            keysFn: m => m.entityId,
            presentFn: (m, k) => m.entityId === k && m.status !== 'D'
        },
        {
            name: 'checkedForEntity',
            keysFn: m => m.entityId,
            presentFn: (m, k) => m.entityId === k && !!m.checked && m.status !== 'D'
        },
        {
            name: 'redFlagTypeId',
            keysFn: m => m.redFlagTypeId,
            presentFn: (m, k) => m.redFlagTypeId === k
        }
    ]
});
    }
}
export default RedFlag;
